import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { ListingCard } from "@/components/ListingCard";
import { HeroSearch } from "@/components/HeroSearch";
import { Button } from "@/components/ui/button";
import { ArrowRight, ChatCircleDots, BookmarkSimple, ShoppingBag, Sparkle } from "@phosphor-icons/react";

const HERO = "https://images.unsplash.com/photo-1548217395-6c6095abb49c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxNzV8MHwxfHNlYXJjaHw0fHxlcGljJTIwdHJhdmVsJTIwbGFuZHNjYXBlfGVufDB8fHx8MTc4NzQwNjM1Mnww&ixlib=rb-4.1.0&q=85";

const features = [
  { icon: ChatCircleDots, title: "AI Concierge", desc: "Chat with Atlas to design a trip around your vibe, budget and season.", to: "/chat" },
  { icon: BookmarkSimple, title: "Trip Boards", desc: "Save destinations into shareable boards and plan together.", to: "/boards" },
  { icon: ShoppingBag, title: "Travel Shop", desc: "Field-tested bags, apparel and gear for every kind of journey.", to: "/shop" },
];

export default function Home() {
  const [listings, setListings] = useState([]);

  useEffect(() => {
    api.get("/listings").then((r) => setListings(r.data.slice(0, 3))).catch(() => {});
  }, []);

  return (
    <div>
      {/* Hero */}
      <section className="relative min-h-[92vh] flex items-end overflow-hidden">
        <img src={HERO} alt="Mountain road" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-black/25 to-black/40" />
        <div className="noise-overlay" />
        <div className="relative max-w-7xl mx-auto px-5 sm:px-8 pb-20 pt-32 w-full">
          <span className="inline-flex items-center gap-2 text-xs tracking-[0.25em] uppercase font-bold text-[#E8B291] mb-6 animate-fade-up">
            <Sparkle size={16} weight="fill" /> AI-crafted journeys
          </span>
          <h1 className="font-display text-white text-4xl sm:text-6xl lg:text-7xl font-bold leading-[0.95] max-w-4xl animate-fade-up" style={{ animationDelay: "80ms" }}>
            The world is wide.<br />Plan yours beautifully.
          </h1>
          <p className="text-white/85 text-base sm:text-lg max-w-xl mt-6 leading-relaxed animate-fade-up" style={{ animationDelay: "160ms" }}>
            Discover extraordinary destinations, get personalised itineraries from our AI concierge,
            and gear up in one seamless place.
          </p>
          <div className="flex flex-wrap gap-3 mt-9 animate-fade-up" style={{ animationDelay: "240ms" }}>
            <HeroSearch />
          </div>
          <div className="flex flex-wrap gap-3 mt-5 animate-fade-up" style={{ animationDelay: "300ms" }}>
            <Link to="/chat">
              <Button className="rounded-full bg-[#2E523A] hover:bg-[#26442f] text-white h-13 px-7 py-6 text-base transition-transform duration-300 hover:-translate-y-1" data-testid="hero-chat-btn">
                Ask the AI Concierge <ArrowRight size={20} className="ml-1" />
              </Button>
            </Link>
            <Link to="/explore">
              <Button variant="outline" className="rounded-full border-white/60 bg-white/10 text-white hover:bg-white hover:text-[#1A1C19] h-13 px-7 py-6 text-base backdrop-blur transition-colors duration-200" data-testid="hero-explore-btn">
                Browse destinations
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="max-w-7xl mx-auto px-5 sm:px-8 py-20">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          {features.map((f, i) => (
            <Link
              key={f.title}
              to={f.to}
              className="group p-8 rounded-2xl border border-[#D2D5CD] bg-white hover:border-[#2E523A]/40 transition-colors duration-200 animate-fade-up"
              style={{ animationDelay: `${i * 80}ms` }}
              data-testid={`feature-${f.title.toLowerCase().replace(/\s/g, "-")}`}
            >
              <div className="h-14 w-14 rounded-2xl bg-[#EAEBE6] flex items-center justify-center mb-5 group-hover:bg-[#2E523A] transition-colors duration-200">
                <f.icon size={28} weight="duotone" className="text-[#2E523A] group-hover:text-white transition-colors duration-200" />
              </div>
              <h3 className="font-display text-xl font-bold mb-2">{f.title}</h3>
              <p className="text-[#63675E] leading-relaxed">{f.desc}</p>
              <span className="inline-flex items-center gap-1 mt-4 text-sm font-semibold text-[#2E523A]">
                Discover <ArrowRight size={16} className="transition-transform duration-200 group-hover:translate-x-1" />
              </span>
            </Link>
          ))}
        </div>
      </section>

      {/* Featured destinations */}
      <section className="max-w-7xl mx-auto px-5 sm:px-8 pb-8">
        <div className="flex items-end justify-between mb-8">
          <div>
            <span className="text-xs tracking-[0.2em] uppercase font-bold text-[#4A6E7C]">Handpicked</span>
            <h2 className="font-display text-3xl sm:text-4xl font-bold mt-2">Featured destinations</h2>
          </div>
          <Link to="/explore" className="hidden sm:inline-flex items-center gap-1 text-sm font-semibold text-[#2E523A] hover:gap-2 transition-all duration-200" data-testid="view-all-destinations">
            View all <ArrowRight size={16} />
          </Link>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {listings.map((l, i) => (
            <ListingCard key={l.id} listing={l} index={i} />
          ))}
        </div>
      </section>
    </div>
  );
}
